import List, { ListTypes } from "devextreme-react/list";
import notify from "../../lib/notify";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTutorial } from "../../contexts/tutorial";
import { useAuth } from "../../contexts/auth";
import { CompanyEnum, RoleEnum, type UserMenuSectionProps } from "../../types";
import SubstitutionPopup from "../substitution-popup/SubstitutionPopup";
import RegulationsPopup from "../regulations-popup/RegulationsPopup";
import { getTutorialSteps, TUTORIAL_MODULES } from "../../config/tutorialSteps";
import "./UserMenuSection.scss";

type MenuItem = {
  text: string;
  icon: string;
  onClick: () => void;
};

export const UserMenuSection = ({
  dropDownButtonRef,
  listRef,
}: UserMenuSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { startStepTour, setCurrentModule } = useTutorial();

  const [popupSubstitutionVisible, setPopupSubstitutionVisible] =
    useState(false);
  const [popupRegulationsVisible, setPopupRegulationsVisible] = useState(false);

  const isManager = user?.roles?.includes(RoleEnum.Kierownik) ?? false;

  const handleTutorialClick = useCallback(() => {
    
      const firstModuleKey = Object.keys(TUTORIAL_MODULES)[0]; 
      const steps = getTutorialSteps(firstModuleKey, t, true);
      
      if (steps.length > 0) {
        setCurrentModule(firstModuleKey);
        startStepTour(steps, firstModuleKey);
      } 
    
  }, [startStepTour, setCurrentModule, t]);

  const menuItems = useMemo<MenuItem[]>(
    () => [
      {
        text: t("section.changePassword"),
        icon: "key",
        onClick: () => {
          navigate("/change-password");
          dropDownButtonRef?.current?.instance().close();
        },
      },
      {
        text: "2FA",
        icon: "lock",
        onClick: () => {
          navigate("/2fa");
          dropDownButtonRef?.current?.instance().close();
        },
      },
      ...(isManager
        ? [
            {
              text: t("section.setReplacment"),
              icon: "pulldown",
              onClick: () => {
                setPopupSubstitutionVisible(true);
              },
            },
          ]
        : []),
      {
        text: t("section.tutorial"),
        icon: "help",
        onClick: handleTutorialClick,
      },
      {
        text: t("section.regulations"),
        icon: "doc",
        onClick: () => {
          setPopupRegulationsVisible(true);
        },
      },
      {
        text: t("section.logOut"),
        icon: "runner",
        onClick: signOut,
      },
    ],
    [dropDownButtonRef, handleTutorialClick, isManager, navigate, signOut, t]
  );

  const onItemClick = useCallback(
    ({ itemData }: ListTypes.ItemClickEvent<MenuItem>) => itemData?.onClick(),
    []
  );

  return (
    <>
      <div className="user-info">
        <div className="user-name">{user?.email}</div>
        <div className="user-name font-thin">{CompanyEnum[user?.companyId!]}</div>
        {(user?.firstName || user?.lastName) && (
          <div className="user-name font-thin">
            {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
          </div>
        )}
        {user?.roles && user.roles.length > 0 && (
          <div className="user-name font-thin">
            {user.roles
              .map((r) => {
                const key = `roles.${r}`;
                const translated = t(key);
                return translated === key ? r : translated;
              })
              .join(", ")}
          </div>
        )}
      </div>
      <List
        ref={listRef}
        elementAttr={{
          class: "user-info-list",
        }}
        onItemClick={onItemClick}
        items={menuItems}
      />
      {popupSubstitutionVisible && user && (
        <SubstitutionPopup
          managerId={user.employeeId || 0}
          managerCompanyId={user.companyId || 0}
          onClose={(passwordChanged: boolean) => {
            setPopupSubstitutionVisible(false);
            if (passwordChanged) {
              notify(
                t("administrationPanel.notifications.userPasswordChanged"),
                "success"
              );
            }
          }}
        />
      )}
      <RegulationsPopup
        visible={popupRegulationsVisible}
        onClose={() => setPopupRegulationsVisible(false)}
      />
    </>
  );
};
